import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@/auth/usePermissions';
import {
  QuickSearch,
  type QuickSearchResult,
} from '@/components/quick-search/QuickSearch';
import { FeatureFlagsRouteSync } from '@/features/feature-flags/FeatureFlagsRouteSync';
import { toQuickSearchResult } from '@/components/quick-search/toQuickSearchResult';
import { toFamilyQuickSearchResult } from '@/components/quick-search/toFamilyQuickSearchResult';
import { Toaster } from '@/components/ui/toaster';
import { usePersonSearch } from '@/hooks/usePersonSearch';
import { useFamilySearch } from '@/hooks/useFamilySearch';

import { PageSkeleton } from './PageSkeleton';
import {
  ShellContext,
  type BreadcrumbItem,
  type ShellContextValue,
} from './ShellContext';
import { TopNav } from './TopNav';

interface AppLayoutProps {
  readonly tenantLogoSrc?: string | null;
  readonly tenantName?: string | null;
}

/**
 * Root route shell: header + main outlet + global QuickSearch dialog.
 *
 * The shell owns the QuickSearch dialog (open/query state) so any descendant
 * route can trigger it through the header without prop-drilling.
 *
 * `usePersonSearch` runs a debounced server-side `GET /persons/search` keyed on
 * last name as the user types (PTV2-1232 #7) — no bulk person list is shipped
 * to the browser. The core BFF's search is structured (last_name / identifier /
 * DOB) with no free-text or first-name parameter, so this matches on last name;
 * first-name / full-name matching needs a BE enhancement. `toQuickSearchResult`
 * adapts summaries to QuickSearch's UI contract.
 *
 * Each route element is responsible for its own sidebar + main composition
 * via `PageWithSidebar` — the shell stays agnostic to per-route chrome.
 */
export function AppLayout({
  tenantLogoSrc = null,
  tenantName = null,
}: AppLayoutProps): ReactElement {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { canBrowsePatients, canCreatePatients } = usePermissions();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState<readonly BreadcrumbItem[] | null>(
    null,
  );

  const patientSearch = usePersonSearch(searchQuery);
  const familySearch = useFamilySearch(searchQuery);

  const searchResults = useMemo<readonly QuickSearchResult[]>(
    () => patientSearch.results.map(toQuickSearchResult),
    [patientSearch.results],
  );

  const familyResults = useMemo<readonly QuickSearchResult[]>(
    () =>
      familySearch.results.map((family) =>
        toFamilyQuickSearchResult(
          family,
          t('quickSearch.familyMemberCount', { count: family.memberCount }),
        ),
      ),
    [familySearch.results, t],
  );

  const isQuickSearchLoading = patientSearch.isLoading || familySearch.isLoading;

  const openSearch = useCallback(() => {
    setSearchOpen(true);
  }, []);

  // Global Cmd+K (mac) / Ctrl+K (win/linux) opens Quick Search from anywhere.
  // The modifier requirement means the shortcut never intercepts plain typing
  // in inputs. Esc close is handled by the Radix Dialog. Gated on browse
  // permission to match the header affordance in `TopNav`.
  useEffect(() => {
    if (!canBrowsePatients) return undefined;
    function onKeyDown(event: KeyboardEvent): void {
      if (
        (event.metaKey || event.ctrlKey) &&
        !event.altKey &&
        event.key.toLowerCase() === 'k'
      ) {
        event.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [canBrowsePatients]);

  const handleSelect = useCallback(
    (result: QuickSearchResult) => {
      setSearchOpen(false);
      setSearchQuery('');
      Promise.resolve(navigate(result.href)).catch(() => {
        // `navigate` can return a Promise; navigation errors are non-fatal.
      });
    },
    [navigate],
  );

  const handleOpenChange = useCallback((next: boolean) => {
    setSearchOpen(next);
    if (!next) setSearchQuery('');
  }, []);

  const handleCreateNew = useCallback((_query: string) => {
    setSearchOpen(false);
    setSearchQuery('');
    void navigate('/patients/new');
  }, [navigate]);

  const handleSetBreadcrumbs = useCallback(
    (next: readonly BreadcrumbItem[] | null) => {
      setBreadcrumbs(next);
    },
    [],
  );

  const shellValue = useMemo<ShellContextValue>(
    () => ({
      openQuickSearch: openSearch,
      breadcrumbs,
      setBreadcrumbs: handleSetBreadcrumbs,
    }),
    [openSearch, breadcrumbs, handleSetBreadcrumbs],
  );

  return (
    <ShellContext.Provider value={shellValue}>
      {/* Refetch the feature-flag map on navigation (D27) — must live inside
          the router, which the FeatureFlagsProvider (in main.tsx) sits above. */}
      <FeatureFlagsRouteSync />
      <div data-region="app" className="flex flex-col h-screen overflow-clip">
        <TopNav
          tenantLogoSrc={tenantLogoSrc}
          tenantName={tenantName}
          onOpenQuickSearch={openSearch}
        />
        <div className="flex-1 min-h-0 overflow-clip">
          <Suspense fallback={<PageSkeleton />}>
            <Outlet />
          </Suspense>
        </div>
        <QuickSearch
          open={searchOpen}
          onOpenChange={handleOpenChange}
          query={searchQuery}
          onQueryChange={setSearchQuery}
          results={searchResults}
          familyResults={familyResults}
          isLoading={isQuickSearchLoading}
          onSelect={handleSelect}
          {...(canCreatePatients ? { onCreateNew: handleCreateNew } : {})}
        />
        <Toaster />
      </div>
    </ShellContext.Provider>
  );
}
